/**
 * Location Form Page Component
 * /admin/locations/new and /admin/locations/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiClient from '../api/client';

const LocationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    latitude: '',
    longitude: '',
    location_type: 'other',
    aliases: '',
    is_active: true,
  });

  useEffect(() => {
    if (isEdit) {
      loadLocation();
    }
  }, [id]);

  const loadLocation = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getLocation(id);
      
      setFormData({
        name: data.name || '',
        address: data.address || '',
        city: data.city || '',
        latitude: data.latitude || '',
        longitude: data.longitude || '',
        location_type: data.location_type || 'other',
        aliases: Array.isArray(data.aliases) ? data.aliases.join(', ') : '',
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
    } catch (err) {
      setError(err.message || 'Failed to load location');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      // Prepare data for API
      const submitData = {
        name: formData.name,
        address: formData.address || null,
        city: formData.city || null,
        location_type: formData.location_type,
        is_active: formData.is_active,
      };

      // Handle coordinates
      if (formData.latitude) {
        submitData.latitude = parseFloat(formData.latitude);
      }
      if (formData.longitude) {
        submitData.longitude = parseFloat(formData.longitude);
      }

      // Handle aliases (convert comma-separated string to array)
      if (formData.aliases) {
        submitData.aliases = formData.aliases.split(',').map(a => a.trim()).filter(a => a);
      } else {
        submitData.aliases = [];
      }

      let response;
      if (isEdit) {
        response = await apiClient.updateLocation(id, submitData);
        setSuccessMessage('Location updated successfully!');
      } else {
        response = await apiClient.createLocation(submitData);
        setSuccessMessage('Location created successfully!');
      }

      // Navigate to location detail page after a short delay
      setTimeout(() => {
        navigate(`/admin/locations/${response.id || id}`);
      }, 1500);
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} location`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/locations')}
            sx={{ mb: 1 }}
          >
            Back to Locations
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Location' : 'New Location'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="name"
                label="Location Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Address */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="address"
                label="Address"
                value={formData.address}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>

            {/* City */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="city"
                label="City"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>

            {/* Location Type */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Location Type</InputLabel>
                <Select
                  name="location_type"
                  value={formData.location_type}
                  onChange={handleChange}
                  label="Location Type"
                >
                  <MenuItem value="airport">Airport</MenuItem>
                  <MenuItem value="hotel">Hotel</MenuItem>
                  <MenuItem value="office">Office</MenuItem>
                  <MenuItem value="camp">Camp</MenuItem>
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Coordinates */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="latitude"
                label="Latitude"
                type="number"
                value={formData.latitude}
                onChange={handleChange}
                slotProps={{ htmlInput: { step: 'any' } }}
                helperText="Optional: GPS coordinate"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="longitude"
                label="Longitude"
                type="number"
                value={formData.longitude}
                onChange={handleChange}
                slotProps={{ htmlInput: { step: 'any' } }}
                helperText="Optional: GPS coordinate"
              />
            </Grid>

            {/* Aliases */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="aliases"
                label="Aliases"
                value={formData.aliases}
                onChange={handleChange}
                helperText="Comma-separated alternative names for this location"
                placeholder="e.g., Abu Dhabi Airport, AUH, ADIA"
              />
            </Grid>

            {/* Active Status */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Submit Button */}
            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/locations')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEdit ? 'Update Location' : 'Create Location'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default LocationForm;

